/**
 * Type definitions for the SLAM Sports AI - College Recruiter Analysis System
 * Ported from theWIRE/SLAMPitchDashboard/lib/types.ts
 * Converted from TypeScript to JavaScript with JSDoc annotations
 *
 * @module types
 */

/**
 * Media type enumeration
 * @readonly
 * @enum {string}
 */
export const MediaType = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  CSV: 'CSV',
  DOCUMENT: 'DOCUMENT',    // PDF, TXT (Gemini native)
  CONTEXT: 'CONTEXT',      // DOCX, XLSX (requires conversion)
  UNKNOWN: 'UNKNOWN'
};

/**
 * @typedef {Object} PitchMetadata
 * @property {string} [pitchType] - Type of pitch (e.g., 'fastball', 'curveball')
 * @property {string} [cameraAngle] - Camera angle (e.g., 'CF', 'Side')
 * @property {string} [pitcherName] - Name of the pitcher
 * @property {string} [date] - Date of the pitch
 * @property {string} [velocity] - Velocity of the pitch
 * @property {string} [source] - Source of the data
 * @property {Object.<string, string>} [additionalData] - Any additional metadata
 */

/**
 * @typedef {Object} MediaItem
 * @property {string} id - Unique identifier for the media item
 * @property {File} file - Original file object
 * @property {string} type - Media type (from MediaType enum)
 * @property {string} previewUrl - Blob URL for preview
 * @property {string} [base64Data] - Loaded asynchronously for API calls
 * @property {string} mimeType - MIME type of the media
 * @property {string} name - Display name of the media item
 * @property {PitchMetadata} [metadata] - Extracted from filename (e.g., Curveball_CF.mov)
 * @property {string} [textContent] - Extracted text for DOCX/XLSX
 * @property {boolean} [isContextFile] - Flag for pre-video context
 * @property {number} [size] - File size in bytes
 * @property {string} [thumbnailUrl] - Thumbnail URL for videos/images
 * @property {number} [duration] - Duration in seconds for videos
 * @property {Object} [dimensions] - Dimensions for images/videos
 * @property {number} [dimensions.width] - Width in pixels
 * @property {number} [dimensions.height] - Height in pixels
 */

/**
 * Context file type - used for pre-analysis documents
 * @typedef {Object} ContextFile
 * @property {string} id - Unique identifier
 * @property {File} file - Original file object
 * @property {string} name - File name
 * @property {'pdf' | 'txt' | 'docx' | 'xlsx' | 'xml'} type - Document type
 * @property {string} [textContent] - Extracted text content
 * @property {'pending' | 'processing' | 'ready' | 'error'} status - Processing status
 * @property {string} [error] - Error message if status is 'error'
 */

/**
 * @typedef {Object} AnalysisResponse
 * @property {string} markdown - Analysis result in markdown format
 * @property {number} timestamp - Unix timestamp of analysis
 */

/**
 * @typedef {Object} TellDetection
 * @property {string} id - Unique identifier
 * @property {string} bodyPart - Body part (e.g., "Glove", "Arm", "Timing")
 * @property {string} tellType - Type of tell (e.g., "Position Variance", "Timing Leak")
 * @property {string} description - Description of the tell
 * @property {number} confidence - Confidence score (0-100)
 * @property {'HIGH' | 'MEDIUM' | 'LOW'} confidenceLevel - Confidence level
 * @property {string[]} pitchTypesAffected - Pitch types affected by this tell
 * @property {string} [recommendation] - Recommendation for fixing
 * @property {string} [timestamp] - Timestamp in video where tell was detected
 * @property {string} [frameReference] - Reference to specific frame
 * @property {string[]} [indicators] - List of visual indicators
 */

/**
 * @typedef {Object} PitchAnalysis
 * @property {number} pitchNumber - Pitch number in sequence
 * @property {string} pitchType - Predicted pitch type
 * @property {string} [groundTruth] - From filename metadata if available
 * @property {string[]} indicators - Visual indicators observed
 * @property {number} confidence - Confidence in prediction (0-100)
 * @property {string} [timestamp] - Video timestamp reference
 * @property {string} [frameRef] - Frame reference
 */

/**
 * @typedef {Object} ExpertAnalysis
 * @property {string} expertName - Name of the expert persona
 * @property {string} expertRole - Role/specialty of the expert
 * @property {string} analysis - The expert's analysis text
 * @property {string[]} [keyPoints] - Key points from the analysis
 * @property {number} [confidence] - Confidence score
 */

/**
 * @typedef {Object} AnalysisExportJSON
 * @property {Object} metadata - Export metadata
 * @property {string} metadata.exportTimestamp - ISO timestamp of export
 * @property {string} metadata.analysisTimestamp - ISO timestamp of analysis
 * @property {string} metadata.version - Export format version
 * @property {string} metadata.generatedBy - Platform name
 * @property {Object} summary - Analysis summary
 * @property {'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE'} summary.overallRiskLevel - Risk level
 * @property {number} summary.overallConfidence - Overall confidence
 * @property {number} summary.totalPitchesAnalyzed - Number of pitches analyzed
 * @property {number} summary.tellsDetected - Number of tells detected
 * @property {string} [summary.primaryTell] - Primary tell identified
 * @property {TellDetection[]} tells - Detected tells
 * @property {PitchAnalysis[]} pitchByPitch - Pitch-by-pitch analysis
 * @property {string[]} recommendations - Recommendations
 * @property {Object[]} media - Media items analyzed
 * @property {string} media.filename - Media filename
 * @property {string} media.type - Media type
 * @property {Object} [media.metadata] - Media metadata
 * @property {string} [media.metadata.pitchType] - Pitch type from filename
 * @property {string} [media.metadata.cameraAngle] - Camera angle
 * @property {string} rawMarkdown - Raw markdown response
 */

/**
 * @typedef {Object} CSVDetailRow
 * @property {number} pitchNumber - Pitch number
 * @property {string} pitchType - Type of pitch
 * @property {string} tellType - Type of tell
 * @property {string} bodyPart - Body part involved
 * @property {number} confidence - Confidence score
 * @property {string} description - Description
 * @property {string} recommendation - Recommendation
 */

/**
 * @typedef {Object} UploadProgress
 * @property {string} fileId - ID of the file being uploaded
 * @property {string} fileName - Name of the file
 * @property {number} progress - Upload progress (0-100)
 * @property {'pending' | 'uploading' | 'complete' | 'error'} status - Upload status
 * @property {string} [error] - Error message if failed
 */

/**
 * @typedef {Object} AnalysisSettings
 * @property {string} model - AI model to use
 * @property {string[]} enabledExperts - List of enabled expert personas
 * @property {'quick' | 'detailed' | 'comprehensive'} analysisMode - Analysis mode
 * @property {boolean} includeCSVData - Whether to include CSV data in analysis
 * @property {boolean} generatePDF - Whether to generate PDF report
 * @property {string} [customPrompt] - Custom prompt additions
 */

/**
 * Helper function to check if a value is a valid MediaType
 * @param {string} value - Value to check
 * @returns {boolean} True if valid MediaType
 */
export function isValidMediaType(value) {
  return Object.values(MediaType).includes(value);
}

/**
 * Get MediaType from file MIME type
 * @param {string} mimeType - MIME type string
 * @returns {string} Corresponding MediaType value
 */
export function getMediaTypeFromMime(mimeType) {
  if (!mimeType) return MediaType.UNKNOWN;

  if (mimeType.startsWith('image/')) return MediaType.IMAGE;
  if (mimeType.startsWith('video/')) return MediaType.VIDEO;
  if (mimeType === 'text/csv' || mimeType.includes('csv')) return MediaType.CSV;
  if (
    mimeType === 'application/pdf' ||
    mimeType === 'text/plain'
  ) {
    return MediaType.DOCUMENT;
  }
  if (
    mimeType.includes('document') ||
    mimeType.includes('word') ||
    mimeType.includes('spreadsheet') ||
    mimeType.includes('excel')
  ) {
    return MediaType.CONTEXT;
  }

  return MediaType.UNKNOWN;
}

/**
 * Get MediaType from file extension
 * @param {string} filename - Filename with extension
 * @returns {string} Corresponding MediaType value
 */
export function getMediaTypeFromExtension(filename) {
  if (!filename) return MediaType.UNKNOWN;

  const ext = filename.split('.').pop()?.toLowerCase();

  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
  const videoExts = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'm4v', 'wmv', 'quicktime'];
  const csvExts = ['csv'];
  const docExts = ['pdf', 'txt'];
  const contextExts = ['docx', 'xlsx', 'doc', 'xls', 'xml'];

  if (imageExts.includes(ext)) return MediaType.IMAGE;
  if (videoExts.includes(ext)) return MediaType.VIDEO;
  if (csvExts.includes(ext)) return MediaType.CSV;
  if (docExts.includes(ext)) return MediaType.DOCUMENT;
  if (contextExts.includes(ext)) return MediaType.CONTEXT;

  return MediaType.UNKNOWN;
}

export default {
  MediaType,
  isValidMediaType,
  getMediaTypeFromMime,
  getMediaTypeFromExtension,
};
